import adjustColorContrast from './adjustColorContrast'
import { parseNumericLabel } from './formatNumber'

export const MAX_PARAMETERS = 50

const PALETTES = {
  magma: ['#000004', '#3b0f70', '#8c2981', '#de4968', '#fcfdbf'],
  viridis: ['#440154', '#31688e', '#35b779', '#fde725'],
  plasma: ['#0d0887', '#cc4778', '#f89540', '#f0f921'],
  spectral: ['#9e0142', '#d53e4f', '#f46d43', '#ffffbf', '#66c2a5', '#3288bd'],
}

const parseHex = (hex) => {
  hex = hex.replace('#', '')
  return [
    parseInt(hex.substring(0, 2), 16),
    parseInt(hex.substring(2, 4), 16),
    parseInt(hex.substring(4, 6), 16),
  ]
}

const toHex = (c) => Math.round(c).toString(16).padStart(2, '0')

const interpolate = (palette, t) => {
  if (palette === 'rainbow') {
    const h = (t * 270) / 360
    const q = 1 // s=1, l=0.5 → q = 0.5*(1+1) = 1
    const p = 0 // 2*0.5 - 1 = 0
    const hue2rgb = (x, y, z) => {
      if (z < 0) z += 1
      if (z > 1) z -= 1
      if (z < 1 / 6) return x + (y - x) * 6 * z
      if (z < 1 / 2) return y
      if (z < 2 / 3) return x + (y - x) * (2 / 3 - z) * 6
      return x
    }
    const r = hue2rgb(p, q, h + 1 / 3)
    const g = hue2rgb(p, q, h)
    const b = hue2rgb(p, q, h - 1 / 3)
    return `#${toHex(r * 255)}${toHex(g * 255)}${toHex(b * 255)}`
  }

  const stops = PALETTES[palette] || PALETTES.spectral
  const scaled = t * (stops.length - 1)
  const lo = Math.floor(scaled)
  const hi = Math.min(lo + 1, stops.length - 1)
  const frac = scaled - lo
  const c0 = parseHex(stops[lo])
  const c1 = parseHex(stops[hi])
  const r = c0[0] + (c1[0] - c0[0]) * frac
  const g = c0[1] + (c1[1] - c0[1]) * frac
  const b = c0[2] + (c1[2] - c0[2]) * frac
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

const generatePaletteColors = (palette) =>
  Array.from({ length: MAX_PARAMETERS }, (_, i) =>
    interpolate(palette, i / (MAX_PARAMETERS - 1)),
  )

export const PALETTE_COLORS = {
  rainbow: generatePaletteColors('rainbow'),
  magma: generatePaletteColors('magma'),
  viridis: generatePaletteColors('viridis'),
  plasma: generatePaletteColors('plasma'),
  spectral: generatePaletteColors('spectral'),
}

export const getPaletteColor = (palette, index) => {
  const colors = PALETTE_COLORS[palette] || PALETTE_COLORS.spectral
  return colors[Math.min(index, colors.length - 1)]
}

export const paramColorIndex = (index, total) =>
  total > 1
    ? Math.round((index * (MAX_PARAMETERS - 1)) / (total - 1))
    : Math.floor((MAX_PARAMETERS - 1) / 2)

const GRADIENT_STOPS = 10
export const buildCorrectedGradient = (
  palette,
  theme,
  direction = 'to right',
) => {
  const colors = Array.from({ length: GRADIENT_STOPS }, (_, i) =>
    adjustColorContrast(
      getPaletteColor(palette, paramColorIndex(i, GRADIENT_STOPS)),
      theme,
    ),
  )
  return `linear-gradient(${direction}, ${colors.join(', ')})`
}

export const gradientFromColors = (colors, direction = 'to bottom') => {
  const stops = (colors ?? []).filter(Boolean)
  if (stops.length === 0) return 'transparent'
  if (stops.length === 1) return stops[0]
  return `linear-gradient(${direction}, ${stops.join(', ')})`
}

export const compareParamLabels = (a, b) => {
  const na = parseNumericLabel(a)
  const nb = parseNumericLabel(b)
  if (na != null && nb != null) {
    return na - nb
  }
  return String(a).localeCompare(String(b))
}

export const sortParamsByLabel = (params, getLabel) =>
  (params ?? []).toSorted((a, b) =>
    compareParamLabels(getLabel(a), getLabel(b)),
  )
